// Copied to mju-aioe/packages/shared/src/schemas/projectPictures.ts — keep the two identical (zod only, no other imports).
/**
 * Project pictures (D193 phase 3, docs/plans/2026-10-diagrammer.md, "Project
 * pictures contract"): the pictures of a whole project — its architecture
 * above the repositories, its infrastructure, data and integrations merged,
 * its roadmap — kept at the project's home like the rest of its work layer
 * (./projectWork.ts): the organisation for a project registered with it, the
 * hub for a team's, the desktop's (or node's) private hub for one kept on this
 * computer (the Personal space). A
 * repository's own pictures stay in its `docs/diagrams/`.
 *
 *   GET    /projects/v1/:id/pictures[?include=spec]   { pictures: ProjectPictureSummary[], people{} }   newest first;
 *                                                     with include=spec each summary carries its `spec`
 *   GET    /projects/v1/:id/pictures/:slug            the picture: summary + `spec` + `svg`; ETag "<version>"
 *   GET    /projects/v1/:id/pictures/:slug/previous   the version before the current one (`spec`, `svg`, `look`,
 *                                                     `version`, `savedAt`, `savedBy`); 404 `no_previous` when none
 *   PUT    /projects/v1/:id/pictures/:slug            { spec, svg, look, edited? } → 201 new | 200 replaced (or unchanged);
 *                                                     If-Match "<version>" → 412 precondition_failed { current };
 *                                                     If-None-Match "*" → 412 when it already exists
 *   DELETE /projects/v1/:id/pictures/:slug            204 (its previous version goes with it); If-Match as above
 *
 * **What is kept.** The spec — the typed JSON the workbench draws from
 * (`shared/diagrams/spec.ts` in the workbench; the server checks only the
 * envelope here, {@link pictureSpecEnvelopeSchema}) — and the SVG the
 * workbench drew from it, checked to be a plain picture ({@link svgProblem}:
 * no script, no event handlers, no embedded HTML, nothing fetched from
 * elsewhere). Size caps: {@link PICTURE_LIMITS} — 413 `too_large` past them,
 * 409 `too_many` past the count. A slug is the picture's name in the project
 * ({@link PICTURE_SLUG_RE}, what the workbench's `diagramSlug` makes).
 *
 * **Versions.** A picture starts at version 1 and goes up by one for every
 * accepted PUT that changes it; a PUT of the same spec, SVG and look is not
 * a new version. The version it replaces is kept, whole, as `previous` — one
 * step back, no further — so the refinement guard can show what a redraw
 * changed and a person can put the old picture back.
 *
 * **Who.** Members of the project read (viewers too); contributors and owners
 * write and remove; anyone else gets 404. The server signs every version with
 * who saved it (`savedBy`: the person, and the agent beside them as `actor`);
 * a client cannot sign for someone else. `edited: true` says a person changed
 * it by hand in the picture editor: the server records `editedBy` and when.
 * Every accepted write is audited and is one line in the project's activity
 * log ({@link pictureActivity}: `picture.saved`, `picture.removed`).
 *
 * **A home that does not keep them.** A home without these routes answers
 * 404 (while it knows the project), 405 or 501: the workbench says "Your
 * organisation does not keep project pictures yet." and goes on.
 *
 * Words for people: picture, saved by, edited by, the version before, put
 * the old picture back.
 */
import { z } from "zod";
export const PROJECT_PICTURES_SCHEMA_VERSION = 1;
/** The caps every home applies. Bytes are UTF-8 bytes of the JSON spec and of the SVG text. */
export const PICTURE_LIMITS = {
    /** The spec, serialised. */
    specBytes: 512 * 1024,
    /** The SVG (a Whiteboard picture embeds its 49 KB handwriting font). */
    svgBytes: 2 * 1024 * 1024,
    /** A PUT body as a whole: the spec, the SVG and the JSON around them. */
    bodyBytes: 3 * 1024 * 1024,
    /** Pictures one project keeps. */
    perProject: 200,
    title: 200,
};
/** A picture's name in the project: lower-case words joined by dashes, at most 60 characters (the workbench's `diagramSlug`). */
export const PICTURE_SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{0,58}[a-z0-9])?$/;
export const pictureSlugSchema = z.string().regex(PICTURE_SLUG_RE, "a picture's name is lower-case letters, digits and dashes, at most 60");
/** What a picture is of and its look: open words, so a newer workbench's kinds and looks need no change here. */
const word = z.string().regex(/^[a-z][a-z0-9-]{0,39}$/, "a short lower-case word");
export const pictureKindSchema = word;
export const pictureLookSchema = word;
/** Who saved a version: the person, and the agent acting for them when one did. */
export const pictureBySchema = z.object({ principal: z.string().max(200).optional(), actor: z.string().max(200).optional() });
/**
 * The part of a spec a home checks: its version, what kind of picture it is,
 * and its title. Everything else is the workbench's to check (its own schema
 * and repair pass) and is kept as it came.
 */
export const pictureSpecEnvelopeSchema = z.looseObject({
    version: z.literal(1),
    kind: pictureKindSchema,
    meta: z.looseObject({ title: z.string().trim().min(1).max(PICTURE_LIMITS.title) }),
});
/** `PUT …/pictures/:slug`. */
export const projectPicturePutSchema = z.object({
    spec: pictureSpecEnvelopeSchema,
    svg: z.string().min(1),
    look: pictureLookSchema,
    /** A person changed it by hand in the picture editor: the server records them as its editor. */
    edited: z.boolean().optional(),
});
/** The error words a home answers with, beside the sentence in `message`. */
export const PICTURE_ERRORS = {
    invalid: "invalid_request",
    notFound: "not_found",
    noPrevious: "no_previous",
    tooLarge: "too_large",
    tooMany: "too_many",
    precondition: "precondition_failed",
};
/** UTF-8 bytes of a string, without Node or the DOM. */
export function utf8Bytes(s) {
    let n = 0;
    for (let i = 0; i < s.length; i++) {
        const c = s.charCodeAt(i);
        if (c < 0x80)
            n += 1;
        else if (c < 0x800)
            n += 2;
        else if (c >= 0xd800 && c <= 0xdbff && i + 1 < s.length) {
            n += 4;
            i++;
        }
        else
            n += 3;
    }
    return n;
}
/**
 * Why an SVG is not a plain picture a home will keep, in a sentence; undefined
 * when it is. A picture is shown inside the app and downloaded by people, so
 * it may hold shapes, text, styles, SMIL animation and fonts embedded as
 * `data:` — never script, event handlers, embedded HTML, or anything fetched
 * from elsewhere.
 */
export function svgProblem(svg) {
    const head = svg.replace(/^﻿/, "").replace(/^\s*(<\?xml[^>]*\?>\s*)?(<!--[\s\S]*?-->\s*)*/, "");
    if (!/^<svg[\s>]/i.test(head))
        return "the picture is not an SVG";
    if (/<!DOCTYPE|<!ENTITY/i.test(svg))
        return "the picture declares entities";
    if (/<script[\s>/]/i.test(svg))
        return "the picture holds a script";
    if (/<foreignObject[\s>/]/i.test(svg))
        return "the picture holds embedded HTML";
    if (/<(iframe|embed|object)[\s>/]/i.test(svg))
        return "the picture embeds another document";
    // Attributes only: a label may say "one = two".
    if (/<[^>]+\son[a-z]+\s*=/i.test(svg))
        return "the picture has event handlers";
    if (/=\s*["']\s*javascript:/i.test(svg))
        return "the picture links to script";
    // Only data: and same-picture (#id) references: nothing is fetched from elsewhere.
    if (/<[^>]+\s(?:href|xlink:href|src)\s*=\s*["']\s*(?!#|data:)/i.test(svg))
        return "the picture links to something outside it";
    if (/url\(\s*["']?\s*(?!#|data:)/i.test(svg))
        return "the picture loads something from outside it";
    if (/@import/i.test(svg))
        return "the picture imports a style sheet";
    return undefined;
}
/**
 * Why a PUT cannot be kept, as the server and the client both check it:
 * `{ status, error, message }`, or undefined when it is fine.
 */
export function picturePutProblem(put) {
    const specBytes = utf8Bytes(JSON.stringify(put.spec));
    if (specBytes > PICTURE_LIMITS.specBytes)
        return { status: 413, error: PICTURE_ERRORS.tooLarge, message: `the picture's source is ${Math.ceil(specBytes / 1024)} KB; a project keeps at most ${PICTURE_LIMITS.specBytes / 1024} KB` };
    const svgBytes = utf8Bytes(put.svg);
    if (svgBytes > PICTURE_LIMITS.svgBytes)
        return { status: 413, error: PICTURE_ERRORS.tooLarge, message: `the picture is ${(svgBytes / 1024 / 1024).toFixed(1)} MB; a project keeps at most ${PICTURE_LIMITS.svgBytes / 1024 / 1024} MB` };
    const svg = svgProblem(put.svg);
    if (svg)
        return { status: 400, error: PICTURE_ERRORS.invalid, message: svg };
    return undefined;
}
const quoted = (title) => `“${title.length > 80 ? `${title.slice(0, 79)}…` : title}”`;
/**
 * The line a write leaves in the project's activity log (./projectWork.ts
 * `ProjectActivityDraft`): `rootId` "" (the project's own layer), the slug as
 * the target, a sentence that follows a person's name.
 */
export function pictureActivity(before, after, slug, edited = false) {
    if (!after)
        return before ? [{ kind: "picture.removed", rootId: "", target: slug, text: `removed the picture ${quoted(before.title)}` }] : [];
    const text = !before ? `saved a new picture ${quoted(after.title)}` : edited ? `edited the picture ${quoted(after.title)}` : `updated the picture ${quoted(after.title)}`;
    return [{ kind: "picture.saved", rootId: "", target: slug, text, detail: { version: after.version, look: after.look, ...(edited ? { edited: true } : {}) } }];
}
//# sourceMappingURL=projectPictures.js.map